import { deiterateAPI } from "../utils/deiterateAPI.util";
import { window } from "../utils/window.util";

export namespace DocumentLibrary {

    export interface ChangeItem {
        elementName: string;
        newValue: any;
        oldValue: any;
    }

    export interface DocHistoryItem {
        changedBy: string;
        changedDate: Date;
        changes: {
            [key: string]: ChangeItem;
        };
    }

    export interface DocEntity {
        componentType: string;
        docCat: string;
        docCriticality: string;
        docDesc: string;
        docHistory: DocHistoryItem[];
        docID: string;
        docIDstatus: "active" | "inactive" | "deleted";
        docLocation: string;
        docOwner: string;
        docPublishInPrivacyPolicy: "Yes" | "No";
        docRisk: string;
        docSubprocessor: "Yes" | "No";
        docTeam: string;
        backupLocation: string;
        backupNeeded: "Yes" | "No";
        backupRetention: string;
        classification: string;
        customFields: any[];
        dataStorageLocations: string[];
        dataTypesShared: string[];
        dataTypesSharedString: string;
        dateAdded: Date;
        label: string;
        lastReviewDate: Date;
        legalBits: string;
        nextReviewDate: Date;
        portability: "Yes" | "No";
        securityReview: "Yes" | "No";
        serial: string;
        socCriticality: string;
        socSubserviceOrganisation: string;
        status: "Active" | "Decommissioned" | "Deleted";
        vendorManagementNeeded: "Yes" | "No";
        vendorWebsite: string;
    }

    export interface DocumentLibraryResponse {
        statusCode?: number;
        message?: string;
        docs?: DocEntity[];
        entry?: DocEntity;
        error?: string | null;
    }

    export class DocInstance {
        private data: DocumentLibrary.DocEntity;

        constructor(data: DocumentLibrary.DocEntity) {
            this.data = data;
        }

        // A getter to access the underlying data if needed
        public get entity(): DocumentLibrary.DocEntity {
            return this.data;
        }

        // An edit method that calls the static Edit function on the Doc class
        public async edit(updates: Partial<DocumentLibrary.DocEntity>): Promise<DocumentLibrary.DocumentLibraryResponse | void> {
            return DocumentLibrary.Document.Edit(this.data.docID, updates);
        }
    }

    export class Document {

        /**
         * Add an doc to the API.
         * @param id - The ID of the doc to add.
         * @param docData - Partial doc data to add.
         * @param callback - Optional callback after response.
         */
        static async Add(
            id: string,
            docData: Partial<DocEntity>,
            callback?: (res: DocumentLibraryResponse) => any
        ): Promise<DocumentLibraryResponse | void> {
            console.log("Calling API Add", { companyID: window.companyID, ...docData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/documentLibrary/add/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...docData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Delete an doc from the API.
         * @param id - The ID of the doc to delete.
         * @param callback - Optional callback after response.
         */
        static async Delete(
            id: string,
            callback?: (res: DocumentLibraryResponse) => any
        ): Promise<DocumentLibraryResponse | void> {
            console.log("Calling API Delete", { companyID: window.companyID });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/documentLibrary/delete/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }


        /**
         * Edit an existing doc in the API.
         * @param id - The ID of the doc to edit.
         * @param docData - Partial doc data to update.
         * @param callback - Optional callback after response.
         */
        static async Edit(
            id: string,
            docData: Partial<DocEntity>,
            callback?: (res: DocumentLibraryResponse) => any
        ): Promise<DocumentLibraryResponse | void> {
            console.log("Calling API Edit", { companyID: window.companyID, ...docData });

            const res = await deiterateAPI.POST(`/deiterateInternalAPI/documentLibrary/edit/${id}`, {
                mode: 'cors',
                body: JSON.stringify({ companyID: window.companyID, ...docData })
            });

            if (res.statusCode === 200) {
                console.log('API Response', res);
                if (callback) return callback(res);
                return res;
            } else {
                console.error('API Error', res);
            }
        }

        /**
         * Fetch all docs from the API.
         */
        static async Get(): Promise<DocumentLibrary.DocEntity[] | undefined>;

        /**
         * Fetch a specific doc by ID from the API.
         * @param id - The ID of the doc to fetch.
         */
        static async Get(id: string | number): Promise<DocInstance | undefined>;

        static async Get(id?: string | number): Promise<DocumentLibrary.DocEntity[] | DocInstance | undefined> {
            if (typeof id === 'undefined' || id === 'all') {
                // Fetch all docs
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/documentLibrary/get/all`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });
                return res.docs;
            } else {
                // Fetch single doc
                const res = await deiterateAPI.POST(`/deiterateInternalAPI/documentLibrary/get/${id}`, {
                    mode: 'cors',
                    body: JSON.stringify({ companyID: window.companyID })
                });

                if (res.entry) {
                    return new DocInstance(res.entry);
                } else {
                    return undefined;
                }
            }
        }
    }

    export const Doc = new Proxy(Document, {
        get(target, prop) {
            const lowerCasedProp = prop.toString().toLowerCase();
            if (lowerCasedProp === 'get') {
                return target.Get;
            } else if (lowerCasedProp === 'add') {
                return target.Add;
            } else if (lowerCasedProp === 'delete') {
                return target.Delete;
            } else if (lowerCasedProp === 'edit') {
                return target.Edit;
            }

            return target[lowerCasedProp];
        }
    });
}